import firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/database';
import _ from 'lodash';
import {globalfunc} from '../globalfunc.js';
import { Result } from 'element-ui';
import { getByUser, getTeamByCompany } from './core/cacheService';
import { calculateNetworkPower } from '../utils/networkPower';
import { deriveRelationshipSignatures, getRelationshipSignatureGroups } from '../utils/relationshipSignatures';
import { toxicBehaviorRiskScoreFromAnswer } from '../utils/toxicBehavior';

const LEGACY_ENPS_QUESTION_ID = '-L1QT78ajNkp6MpsLkBu'
const LEGACY_NPS_QUESTION_ID = '-M4F5P10foOhqehfK3lv'
const DEFAULT_WHO5_QUESTION_IDS = ['-MdMv_XgThL0o2iunlhE', '-MdMw8kfpZtxnppGUA1f', '-MdMyavdF-YvuyzW6cA5', '-MdMz-TodEXbj3AC68oP', '-MdMzwfySVTjhOAGoxTP']
const DEFAULT_CULTURE_QUESTION_IDS = ['-MjPYFeALtFMAJkTEiFn', '-MjPZ15ImFhkjDiMI3gH', '-MjPZVq3sRBZJwtr7_Tx', '-MjP_2__thuvnVc_RY0k', '-MjP_R_bWBdQvFhf5BTY']
const TOXIC_BEHAVIOR_QUESTION_TEXT = 'How often do you experience or witness harmful behavior at work?'
const relationshipAttributeScoresByCompany: Record<string, Promise<Record<string, any>>> = {}

function normalizeOrganizationTree(organization: any, teamMaster: any) {
  const organization1level: any = {}

  _.forEach(organization, (scope: any, teamId: string) => {
    organization1level[teamId] = {}
    _.forEach(scope, (_value: any, key: string) => {
      organization1level[teamId][key] = true
    })
    organization1level[teamId][teamId] = true
  })

  _.forEach(teamMaster, (_team: any, teamId: string) => {
    if (!organization1level[teamId]) {
      organization1level[teamId] = {}
    }
    organization1level[teamId][teamId] = true
  })

  return organization1level
}

function mapTeamsByKey(teams: any[]) {
  const teamMaster: any = {}

  _.forEach(teams, (team: any) => {
    if (team && team.key) {
      teamMaster[team.key] = team
    }
  })

  return teamMaster
}

function optionalNumber(...values: any[]): number | null {
  for (let i = 0; i < values.length; i++) {
    const value = values[i]
    if (value === undefined || value === null || value === '') continue
    const numberValue = Number(value)
    if (Number.isFinite(numberValue)) return numberValue
  }

  return null
}

function optionalString(...values: any[]): string {
  for (let i = 0; i < values.length; i++) {
    const value = values[i]
    if (value === undefined || value === null) continue
    const stringValue = String(value).trim()
    if (stringValue) return stringValue
  }

  return ''
}

function chunkItems<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = []
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size))
  }
  return chunks
}

function normalizeEmail(value: any): string {
  return String(value || '').replace(/\|\|/g, '.').trim()
}

function dateKey(value: any): string {
  if (!value) return ''
  if (typeof value === 'string') return value.slice(0, 10)
  const date = typeof value.toDate === 'function' ? value.toDate() : new Date(value)
  if (isNaN(date.getTime())) return ''
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

function todayDateKey(): string {
  return dateKey(new Date())
}

function previousQuarterStartDateKey(date = new Date()): string {
  const month = date.getMonth()
  const quarterStartMonth = Math.floor(month / 3) * 3
  return dateKey(new Date(date.getFullYear(), quarterStartMonth - 3, 1))
}

function metricNumberFromSources(sources: any[], fields: string[]): number | null {
  const containers: any[] = []
  sources.forEach((source) => {
    if (!source || typeof source !== 'object') return
    containers.push(source)
    if (source.metrics && typeof source.metrics === 'object') containers.push(source.metrics)
    if (source.scores && typeof source.scores === 'object') containers.push(source.scores)
    if (source.latest_scores && typeof source.latest_scores === 'object') containers.push(source.latest_scores)
    if (source.attributes && typeof source.attributes === 'object') containers.push(source.attributes)
  })

  for (let i = 0; i < containers.length; i++) {
    for (let j = 0; j < fields.length; j++) {
      const value = containers[i][fields[j]]
      const numberValue = optionalNumber(value)
      if (numberValue !== null) return numberValue
    }
  }

  return null
}

function applyRelationshipMetricScores(userObj: any, metricSources: any[]) {
  const impactRating = metricNumberFromSources(metricSources, ['impact_rating', 'impactRating'])
  userObj.impactRating = impactRating === null ? null : Math.round(impactRating)
  userObj.impact_rating = userObj.impactRating
  userObj.healthBar = metricNumberFromSources(metricSources, ['healthbar', 'healthBar', 'happiness'])
  userObj.healthbar = userObj.healthBar
  userObj.who5 = metricNumberFromSources(metricSources, ['who5', 'computed_who5', 'WHO-5', 'wellbeing', 'well_being'])
  userObj.nps = metricNumberFromSources(metricSources, ['nps', 'NPS', 'nps_score', 'npsScore', 'net_promoter_score'])
  userObj.NPS = userObj.nps
  userObj.enps = metricNumberFromSources(metricSources, ['enps', 'eNPS', 'ENPS', 'e_nps', 'enps_score', 'enpsScore', 'employee_nps', 'employee_net_promoter_score'])
  userObj.eNPS = userObj.enps
  userObj.culture = metricNumberFromSources(metricSources, ['culture_index', 'cultureIndex', 'culture', 'culture_score', 'cultureScore'])
  userObj.culture_index = userObj.culture
  userObj.culture_add_percentile = metricNumberFromSources(metricSources, ['culture_add_percentile', 'cultureAddPercentile'])
  userObj.toxic = metricNumberFromSources(metricSources, ['toxic_behaviors', 'toxicBehaviors', 'toxic_behaviour', 'toxicBehaviour', 'toxic_behavior', 'toxicBehavior', 'toxicity', 'toxic', 'toxic_score', 'toxicScore'])
  userObj.toxic_behaviors = userObj.toxic
}

function isReportCardQuestionAvailableForCompany(question: any, companyId: string) {
  if (!question) return false
  return !question.company || question.company === 'main' || question.company === companyId
}

function normalizeQuestionText(value: any): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[’']/g, "'")
    .replace(/\s+/g, ' ')
}

function questionTextValues(question: any): string[] {
  if (!question) return []
  const values = [
    question.question_en,
    question.question,
    question.quest_name_en,
    question.quest_name,
    question.questions && question.questions.en,
  ]

  return values
    .map(normalizeQuestionText)
    .filter(Boolean)
}

function isToxicBehaviorQuestion(question: any): boolean {
  const target = normalizeQuestionText(TOXIC_BEHAVIOR_QUESTION_TEXT)
  return questionTextValues(question).some((text) => {
    return text === target || text.includes('harmful behavior at work')
  })
}

function buildRelationshipQuestionGroups(questionMaster: Record<string, any>, companyId: string) {
  const questions = Object.values(questionMaster).filter((question: any) => {
    return isReportCardQuestionAvailableForCompany(question, companyId)
  })

  const enps = _.uniq(questions
    .filter((question: any) => question && question.report_card === 'Y' && question.nps === 'eNPS')
    .map((question: any) => question.key)
    .filter(Boolean))

  const nps = _.uniq(questions
    .filter((question: any) => question && question.report_card === 'Y' && question.nps && question.nps !== 'eNPS')
    .map((question: any) => question.key)
    .filter(Boolean))

  const culture = _.uniq(questions
    .filter((question: any) => question && question.report_card === 'Y' && question.culture_index === true && question.company === companyId)
    .map((question: any) => question.key)
    .filter(Boolean))

  const toxic = _.uniq(questions
    .filter((question: any) => isToxicBehaviorQuestion(question))
    .map((question: any) => question.key)
    .filter(Boolean))

  return {
    eNPS: enps.length > 0 ? enps : [LEGACY_ENPS_QUESTION_ID],
    NPS: nps.length > 0 ? nps : [LEGACY_NPS_QUESTION_ID],
    culture: culture.length > 0 ? culture : [...DEFAULT_CULTURE_QUESTION_IDS],
    toxic,
  }
}

async function fetchRelationshipQuestionMaster(companyId: string) {
  const questionMaster: Record<string, any> = {}
  const addQuestion = (doc: any) => {
    const data = doc.val()
    if (!data) return
    data.key = doc.key
    if (isReportCardQuestionAvailableForCompany(data, companyId)) {
      questionMaster[data.key] = data
    }
  }

  const snapshot = await firebase.database().ref('questions').orderByChild('report_card').equalTo('Y').once('value')
  snapshot.forEach(addQuestion)

  const toxicQuestionQueries = ['question_en', 'question', 'questions/en']
  for (let i = 0; i < toxicQuestionQueries.length; i++) {
    try {
      const toxicSnapshot = await firebase.database().ref('questions')
        .orderByChild(toxicQuestionQueries[i])
        .equalTo(TOXIC_BEHAVIOR_QUESTION_TEXT)
        .once('value')
      toxicSnapshot.forEach(addQuestion)
    } catch (error) {
      // If one exact text lookup is not indexed for a tenant, the rest of the
      // relationship map should still load from the report-card questions.
      // eslint-disable-next-line no-console
      console.warn('[relationships] Unable to look up toxic behavior question metadata', error)
    }
  }

  return questionMaster
}

async function fetchLatestQuestionDates(companyId: string, questionIds: string[]) {
  const latestByQuestion: Record<string, string> = {}
  const ids = _.uniq((questionIds || []).filter(Boolean))
  const chunks = chunkItems(ids, 10)
  const snapshots = await Promise.all(chunks.map((chunk) => {
    return firebase.firestore().collection('company_questions')
      .where('company', '==', companyId)
      .where('quest_id', 'in', chunk)
      .get()
  }))

  snapshots.forEach((snapshot) => {
    snapshot.forEach((doc: any) => {
      const data = doc.data()
      const questId = data && data.quest_id ? String(data.quest_id) : ''
      const date = data && data.date ? String(data.date) : ''
      if (!questId || !date) return
      if (!latestByQuestion[questId] || date > latestByQuestion[questId]) {
        latestByQuestion[questId] = date
      }
    })
  })

  return latestByQuestion
}

function setLatestUserScore(scores: Record<string, any>, email: string, key: string, value: number | null, date: string) {
  if (!email || value === null) return
  if (!scores[email]) scores[email] = {}
  const dateKey = `${key}_date`
  if (!scores[email][dateKey] || !date || date >= scores[email][dateKey]) {
    scores[email][key] = value
    scores[email][dateKey] = date || scores[email][dateKey] || ''
  }
}

function cultureScoreForAnswer(questionMaster: Record<string, any>, questId: string, answer: any): number | null {
  if (!questId || !questionMaster || !questionMaster[questId]) return null
  const answerNumber = optionalNumber(answer)
  if (answerNumber === null) return null
  const result = globalfunc.calculateCultureScore(questionMaster, questId, { [String(answerNumber)]: 1 })
  if (!result || !result.total) return null
  return Math.round((Number(result.dataSum || 0) * 100) / Number(result.total))
}

function toxicRiskScoreForAnswer(answer: any): number | null {
  return toxicBehaviorRiskScoreFromAnswer(answer)
}

function normalizeWho5AnswerValue(value: any): number | null {
  const parsed = optionalNumber(value)
  if (parsed === null) return null
  if (parsed >= 0 && parsed <= 5) return 5 - parsed
  return parsed
}

function calculateWho5Score(answerSet: Record<string, number>): number | null {
  let dataSum = 0
  let maximum = 0

  for (let i = 0; i < DEFAULT_WHO5_QUESTION_IDS.length; i++) {
    const answer = Number(answerSet[DEFAULT_WHO5_QUESTION_IDS[i]])
    if (isNaN(answer)) return null
    dataSum += answer
    maximum += 5
  }

  if (maximum === 0) return null
  return Math.round((dataSum * 1000) / maximum) / 10
}

function ingestWho5Snapshot(snapshot: any, answersByEmail: Record<string, Record<string, Record<string, number>>>) {
  if (!snapshot) return

  snapshot.forEach((doc: any) => {
    const data = doc.data()
    const email = normalizeEmail(data && (data.users || data.user || data.email))
    const date = dateKey(data && data.date)
    const questId = data && data.quest_id ? String(data.quest_id) : ''
    const answer = normalizeWho5AnswerValue(data && data.answer)
    if (!email || !date || !questId || answer === null) return

    if (!answersByEmail[email]) answersByEmail[email] = {}
    if (!answersByEmail[email][date]) answersByEmail[email][date] = {}
    answersByEmail[email][date][questId] = answer
  })
}

async function fetchWho5Scores(companyId: string) {
  const scores: Record<string, any> = {}
  const answersByEmail: Record<string, Record<string, Record<string, number>>> = {}
  const startDate = previousQuarterStartDateKey()
  const endDate = todayDateKey()

  try {
    const snapshot = await firebase.firestore().collection('users_questions')
      .where('company', '==', companyId)
      .where('quest_id', 'in', DEFAULT_WHO5_QUESTION_IDS)
      .where('date', '>=', startDate)
      .where('date', '<=', endDate)
      .get()
    ingestWho5Snapshot(snapshot, answersByEmail)
  } catch (error) {
    // Some Firestore projects require a composite index for the grouped query.
    // The split-query fallback keeps the map usable without asking users to act.
    // eslint-disable-next-line no-console
    console.warn('[relationships] Unable to load WHO-5 in a single query, falling back to split queries', error)
    const snapshots = await Promise.all(DEFAULT_WHO5_QUESTION_IDS.map((questionId) => {
      return firebase.firestore().collection('users_questions')
        .where('company', '==', companyId)
        .where('quest_id', '==', questionId)
        .where('date', '>=', startDate)
        .where('date', '<=', endDate)
        .get()
        .catch((e) => {
          // eslint-disable-next-line no-console
          console.warn('[relationships] Unable to load WHO-5 question answers', questionId, e)
          return null
        })
    }))

    snapshots.forEach((snapshot) => ingestWho5Snapshot(snapshot, answersByEmail))
  }

  Object.keys(answersByEmail).forEach((email) => {
    const dates = Object.keys(answersByEmail[email]).sort().reverse()
    for (let i = 0; i < dates.length; i++) {
      const answerSet = answersByEmail[email][dates[i]]
      const hasCompleteSet = DEFAULT_WHO5_QUESTION_IDS.every((questionId) => answerSet[questionId] !== undefined)
      if (!hasCompleteSet) continue
      const score = calculateWho5Score(answerSet)
      if (score === null) continue
      scores[email] = {
        who5: score,
        computed_who5: score,
        who5_date: dates[i],
      }
      break
    }
  })

  return scores
}

async function fetchLatestUserQuestionScores(companyId: string, questionMaster: Record<string, any>, groups: Record<string, string[]>) {
  const scores: Record<string, any> = {}
  const cultureScores: Record<string, Array<{ score: number; date: string }>> = {}
  const scoreConfig = [
    { key: 'enps', questionIds: groups.eNPS || [] },
    { key: 'nps', questionIds: groups.NPS || [] },
    { key: 'culture', questionIds: groups.culture || [] },
    { key: 'toxic', questionIds: groups.toxic || [] },
  ]
  const allQuestionIds = _.uniq(scoreConfig.reduce((acc: string[], item) => acc.concat(item.questionIds), []))
  const latestDates = await fetchLatestQuestionDates(companyId, allQuestionIds)

  for (let i = 0; i < scoreConfig.length; i++) {
    const config = scoreConfig[i]
    for (let j = 0; j < config.questionIds.length; j++) {
      const questionId = config.questionIds[j]
      const latestDate = latestDates[questionId]
      if (!latestDate && config.key !== 'toxic') continue
      const question = questionMaster[questionId] || {}
      let query: any = firebase.firestore().collection('users_questions')
        .where('company', '==', companyId)
        .where('quest_id', '==', questionId)
        .where('status', '==', 'close')

      if (latestDate) {
        query = question.rolling === true
          ? query.where('date', '>=', latestDate)
          : query.where('date', '==', latestDate)
      }

      const snapshot = await query.get()
      snapshot.forEach((doc: any) => {
        const data = doc.data()
        const email = normalizeEmail(data && (data.users || data.user || data.email))
        if (!email || data.answer === undefined || data.answer === null || data.answer === '') return
        const date = data.date ? String(data.date) : latestDate

        if (config.key === 'culture') {
          const score = cultureScoreForAnswer(questionMaster, questionId, data.answer)
          if (score === null) return
          if (!cultureScores[email]) cultureScores[email] = []
          cultureScores[email].push({ score, date })
          return
        }

        if (config.key === 'toxic') {
          setLatestUserScore(scores, email, config.key, toxicRiskScoreForAnswer(data.answer), date)
          return
        }

        setLatestUserScore(scores, email, config.key, optionalNumber(data.answer), date)
      })
    }
  }

  Object.keys(cultureScores).forEach((email) => {
    const latestDate = cultureScores[email].reduce((latest, item) => item.date > latest ? item.date : latest, '')
    const recentScores = cultureScores[email].filter(item => item.date === latestDate)
    const total = recentScores.reduce((sum, item) => sum + item.score, 0)
    const value = recentScores.length > 0 ? Math.round(total / recentScores.length) : null
    setLatestUserScore(scores, email, 'culture', value, latestDate)
  })

  return scores
}

async function fetchToxicBehaviorScoresByQuestionText(companyId: string, questionMaster: Record<string, any>) {
  const scores: Record<string, any> = {}
  const fields = ['quest_name_en', 'quest_name']
  for (let i = 0; i < fields.length; i++) {
    try {
      const snapshot = await firebase.firestore().collection('users_questions')
        .where('company', '==', companyId)
        .where('status', '==', 'close')
        .where(fields[i], '==', TOXIC_BEHAVIOR_QUESTION_TEXT)
        .get()

      snapshot.forEach((doc: any) => {
        const data = doc.data()
        const email = normalizeEmail(data && (data.users || data.user || data.email))
        if (!email || data.answer === undefined || data.answer === null || data.answer === '') return
        const questId = data.quest_id ? String(data.quest_id) : ''
        const question = questionMaster[questId] || {
          key: questId,
          question_en: data.quest_name_en,
          question: data.quest_name,
        }
        if (questId && !questionMaster[questId]) questionMaster[questId] = question
        const date = data.date ? String(data.date) : ''
        setLatestUserScore(scores, email, 'toxic', toxicRiskScoreForAnswer(data.answer), date)
      })
    } catch (error) {
      // The exact text fallback is best-effort. The preferred path uses the
      // question metadata and quest_id above.
      // eslint-disable-next-line no-console
      console.warn('[relationships] Unable to load toxic behavior answers by question text', error)
    }
  }

  return scores
}

async function fetchRelationshipAttributeScores(companyId: string) {
  if (!companyId) return {}
  if (!relationshipAttributeScoresByCompany[companyId]) {
    relationshipAttributeScoresByCompany[companyId] = (async () => {
      try {
        const questionMaster = await fetchRelationshipQuestionMaster(companyId)
        const groups = buildRelationshipQuestionGroups(questionMaster, companyId)
        const [questionScores, who5Scores] = await Promise.all([
          fetchLatestUserQuestionScores(companyId, questionMaster, groups),
          fetchWho5Scores(companyId),
        ])
        const hasToxicScores = Object.values(questionScores).some((score: any) => {
          return score && score.toxic !== undefined && score.toxic !== null
        })
        const toxicScores = hasToxicScores
          ? {}
          : await fetchToxicBehaviorScoresByQuestionText(companyId, questionMaster)

        return _.merge({}, questionScores, who5Scores, toxicScores)
      } catch (error) {
        // Relationship map should still render from rel_maps if survey enrichment fails.
        // eslint-disable-next-line no-console
        console.warn('[relationships] Unable to load relationship attribute scores', error)
        return {}
      }
    })()
  }

  return relationshipAttributeScoresByCompany[companyId]
}

function emailKeyFromEmail(value: any): string {
  return normalizeEmail(value).replace(/\./g, '||')
}

function emailFromKey(value: any): string {
  return normalizeEmail(value)
}

function relationshipEdgeList(relMap: any): any[] {
  const edges = relMap && relMap.edges
  if (!edges) return []
  return Array.isArray(edges) ? edges : Object.values(edges)
}

function edgeEndpoint(edge: any, field: 'node1' | 'node2'): string {
  return normalizeEmail(edge && edge[field])
}

function edgeWeight(edge: any): number {
  return optionalNumber(edge && (edge.rel_weight !== undefined ? edge.rel_weight : edge.weight)) || 0
}

function pairKey(emailA: string, emailB: string): string {
  return emailA < emailB ? `${emailA}_${emailB}` : `${emailB}_${emailA}`
}

function relationshipMapMatchesCompany(relMap: any, companyId: string): boolean {
  return Boolean(relMap && (!relMap.comp_id || relMap.comp_id === companyId))
}

function topNeighborKeysFromRelMap(relMap: any, profileEmail: string, limit: number): string[] {
  const weightsByKey: Record<string, number> = {}
  const normalizedProfileEmail = normalizeEmail(profileEmail)

  relationshipEdgeList(relMap).forEach((edge: any) => {
    const node1 = edgeEndpoint(edge, 'node1')
    const node2 = edgeEndpoint(edge, 'node2')
    if (!node1 || !node2) return
    const otherEmail = node1 === normalizedProfileEmail
      ? node2
      : node2 === normalizedProfileEmail
        ? node1
        : ''
    if (!otherEmail || otherEmail === normalizedProfileEmail) return

    const key = emailKeyFromEmail(otherEmail)
    weightsByKey[key] = Math.max(weightsByKey[key] || 0, edgeWeight(edge))
  })

  return Object.keys(weightsByKey)
    .sort((a, b) => {
      const weightDelta = weightsByKey[b] - weightsByKey[a]
      if (weightDelta !== 0) return weightDelta
      return a.localeCompare(b)
    })
    .slice(0, Math.max(limit, 0))
}

async function fetchProfileUserMaster(rootState: any, companyId: string, userKeys: string[], profileFallback: any = {}) {
  const userMaster: any = {}
  const allUser = rootState.main && rootState.main.allUser ? rootState.main.allUser : {}
  const uniqueKeys = _.uniq(userKeys.filter(Boolean))

  uniqueKeys.forEach((keyId: string) => {
    if (allUser && allUser[keyId]) {
      userMaster[keyId] = allUser[keyId]
    }
  })

  const profileKey = emailKeyFromEmail(profileFallback && (profileFallback.email || profileFallback.emailKey))
  if (profileKey && uniqueKeys.includes(profileKey) && !userMaster[profileKey]) {
    userMaster[profileKey] = Object.assign({}, profileFallback, {
      email: emailFromKey(profileKey),
      company: profileFallback.company || companyId,
    })
  }

  const missingKeys = uniqueKeys.filter((keyId: string) => !userMaster[keyId])
  const fetchedUsers = await Promise.all(missingKeys.map(async (keyId: string) => {
    try {
      const userData = await getByUser(emailFromKey(keyId))
      return { keyId, userData }
    } catch (error) {
      return { keyId, userData: null }
    }
  }))

  fetchedUsers.forEach(({ keyId, userData }: any) => {
    if (!userData || (userData.company && userData.company !== companyId)) return
    userMaster[keyId] = Object.assign({}, userData, {
      email: normalizeEmail(userData.email || emailFromKey(keyId)),
      key: userData.key || keyId,
    })
  })

  return userMaster
}

async function fetchProfileRelMaps(companyId: string, userKeys: string[]) {
  const relMaster: any = {}
  const uniqueKeys = _.uniq(userKeys.filter(Boolean))
  const relMaps = await Promise.all(uniqueKeys.map(async (keyId: string) => {
    try {
      const relMap = (await firebase.database().ref(`/rel_maps/${keyId}`).once('value')).val()
      return { keyId, relMap }
    } catch (error) {
      return { keyId, relMap: null }
    }
  }))

  relMaps.forEach(({ keyId, relMap }: any) => {
    if (!relationshipMapMatchesCompany(relMap, companyId)) return
    relMaster[keyId] = relMap
  })

  return relMaster
}

function profileImageFromSources(item: any, userData: any): string {
  return optionalString(
    item && item.pic,
    item && item.profileImage,
    item && item.profilepic_small,
    item && item.profilepic_crop,
    item && item.profilepic,
    item && item.profilePic,
    item && item.large_pic,
    item && item.largePic,
    userData && userData.pic,
    userData && userData.profileImage,
    userData && userData.profilepic_small,
    userData && userData.profilepic_crop,
    userData && userData.profilepic,
    userData && userData.profilePic,
    userData && userData.large_pic,
    userData && userData.largePic,
    userData && userData.avatar,
    userData && userData.photoURL
  )
}

function buildRelationshipRowsFromRelMaps(
  relMaster: any,
  userMaster: any,
  teamMaster: any,
  signatures: any[],
) {
  const dataAllAttribute: any = {}
  const dataRelWeight: any = {}
  const managementChainCache: Record<string, string[]> = {}
  const managementDistanceCache: Record<string, number> = {}

  _.forEach(relMaster, (item: any, keyId: string) => {
    const userEmail = emailFromKey(keyId)
    relationshipEdgeList(item).forEach((edge: any) => {
      const node1 = edgeEndpoint(edge, 'node1')
      const node2 = edgeEndpoint(edge, 'node2')
      if (!node1 || !node2 || (node1 !== userEmail && node2 !== userEmail)) return
      const key = pairKey(node1, node2)
      dataRelWeight[key] = Math.max(dataRelWeight[key] || 0, edgeWeight(edge))
    })
  })

  _.forEach(relMaster, (item: any, keyId: string) => {
    const userEmail = emailFromKey(keyId)
    const userData = userMaster[keyId] || {}
    if (!item && !userData) return

    const firstName = optionalString(item && item.firstname, userData.firstname)
    const lastName = optionalString(item && item.lastname, userData.lastname)
    const teamId = optionalString(userData.team, item && item.team, item && item.teamId)
    const team = teamId && teamMaster ? teamMaster[teamId] : null
    const metricSources = [item, userData]
    const userObj: any = {}

    userObj.firstname = firstName
    userObj.lastname = lastName
    userObj.fullName = optionalString(`${firstName} ${lastName}`.trim(), userEmail)
    userObj.cuttingName = optionalString(
      item && item.cuttingName,
      `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
    )
    userObj.email = userEmail
    userObj.emailKey = keyId
    userObj.profileImage = profileImageFromSources(item, userData)
    userObj.boss = optionalString(userData.boss, item && item.boss)
    userObj.teamId = teamId
    userObj.teamName = optionalString(
      team && team.name,
      userData.teamName,
      userData.team_name,
      item && item.teamName,
      item && item.team_name
    )
    userObj.teamColor = optionalString(
      team && team.color,
      userData.teamColor,
      userData.team_color,
      item && item.teamColor,
      item && item.team_color,
      '#747474'
    )
    const impactRating = metricNumberFromSources(metricSources, ['impact_rating', 'impactRating'])
    userObj.impactRating = impactRating === null ? null : Math.round(impactRating)
    userObj.impact_rating = userObj.impactRating
    userObj.healthBar = metricNumberFromSources(metricSources, ['healthbar', 'healthBar', 'happiness'])
    userObj.healthbar = userObj.healthBar
    userObj.who5 = metricNumberFromSources(metricSources, ['who5', 'computed_who5', 'WHO-5', 'wellbeing', 'well_being'])
    userObj.nps = metricNumberFromSources(metricSources, ['nps', 'NPS', 'nps_score', 'npsScore', 'net_promoter_score'])
    userObj.NPS = userObj.nps
    userObj.enps = metricNumberFromSources(metricSources, ['enps', 'eNPS', 'ENPS', 'e_nps', 'enps_score', 'enpsScore', 'employee_nps', 'employee_net_promoter_score'])
    userObj.eNPS = userObj.enps
    userObj.culture = metricNumberFromSources(metricSources, ['culture_index', 'cultureIndex', 'culture', 'culture_score', 'cultureScore'])
    userObj.culture_index = userObj.culture
    userObj.culture_add_percentile = metricNumberFromSources(metricSources, ['culture_add_percentile', 'cultureAddPercentile'])
    userObj.toxic = metricNumberFromSources(metricSources, ['toxic_behaviors', 'toxicBehaviors', 'toxic_behaviour', 'toxicBehaviour', 'toxic_behavior', 'toxicBehavior', 'toxicity', 'toxic', 'toxic_score', 'toxicScore'])
    userObj.toxic_behaviors = userObj.toxic
    userObj.inner_count = 0
    userObj.outer_count = 0
    userObj.strong_count = 0
    userObj.total_count = 0
    userObj.innerMembers = []
    userObj.outerMembers = []
    userObj.strongMembers = []
    userObj.totalMembers = []
    userObj.me_ratio = -1
    userObj.signatures_count = 0
    userObj.signature_groups = []
    userObj.network_roles_count = 0

    if (signatures.length > 0) {
      userObj.signatures = deriveRelationshipSignatures(item, signatures)
      userObj.signatures_count = userObj.signatures.length
      userObj.signature_groups = getRelationshipSignatureGroups(userObj.signatures)
      userObj.network_roles_count = userObj.signature_groups.length
    }

    const nodeCheck: any = {}
    relationshipEdgeList(item).forEach((edge: any) => {
      const node1 = edgeEndpoint(edge, 'node1')
      const node2 = edgeEndpoint(edge, 'node2')
      if (!node1 || !node2 || (node1 !== userEmail && node2 !== userEmail)) return

      const dedupeKey = pairKey(node1, node2)
      if (nodeCheck[dedupeKey]) return
      nodeCheck[dedupeKey] = true

      const targetEmail = node1 === userEmail ? node2 : node1
      const targetKey = emailKeyFromEmail(targetEmail)
      if (!userMaster[targetKey] && !relMaster[targetKey]) return

      const managementDistance = findManagementDistance(keyId, targetKey, userMaster, managementChainCache, managementDistanceCache)
      if (managementDistance <= 2) {
        userObj.inner_count += 1
        userObj.innerMembers.push(targetKey)
      } else {
        userObj.outer_count += 1
        userObj.outerMembers.push(targetKey)
      }

      if ((dataRelWeight[dedupeKey] || 0) >= 0.66) {
        userObj.strong_count += 1
        userObj.strongMembers.push(targetKey)
      }
      userObj.totalMembers.push(targetKey)
    })
    userObj.total_count = userObj.inner_count + userObj.outer_count

    dataAllAttribute[keyId] = userObj
  })

  _.forEach(dataAllAttribute, (item: any, keyId: string) => {
    if (item.boss) {
      const keyIdBoss = emailKeyFromEmail(item.boss)
      if (dataAllAttribute[keyIdBoss] && dataAllAttribute[keyIdBoss].total_count > 0 && dataAllAttribute[keyId].total_count > 0) {
        dataAllAttribute[keyId].me_ratio = globalfunc.round(dataAllAttribute[keyIdBoss].total_count / dataAllAttribute[keyId].total_count, 1)
      }
    }
  })

  _.forEach(dataAllAttribute, (item: any, keyId: string) => {
    dataAllAttribute[keyId].totalArrData = []
    dataAllAttribute[keyId].innerArrData = []
    dataAllAttribute[keyId].outerArrData = []
    dataAllAttribute[keyId].strongArrData = []

    item.innerMembers.forEach((memberKey: string) => {
      if (dataAllAttribute[memberKey]) {
        dataAllAttribute[keyId].innerArrData.push(dataAllAttribute[memberKey])
        dataAllAttribute[keyId].totalArrData.push(dataAllAttribute[memberKey])
      }
    })
    item.outerMembers.forEach((memberKey: string) => {
      if (dataAllAttribute[memberKey]) {
        dataAllAttribute[keyId].outerArrData.push(dataAllAttribute[memberKey])
        dataAllAttribute[keyId].totalArrData.push(dataAllAttribute[memberKey])
      }
    })
    item.strongMembers.forEach((memberKey: string) => {
      if (dataAllAttribute[memberKey]) {
        dataAllAttribute[keyId].strongArrData.push(dataAllAttribute[memberKey])
      }
    })

    dataAllAttribute[keyId].innerArrData = _.orderBy(dataAllAttribute[keyId].innerArrData, ['total_count', 'fullName'], ['desc', 'asc'])
    dataAllAttribute[keyId].outerArrData = _.orderBy(dataAllAttribute[keyId].outerArrData, ['total_count', 'fullName'], ['desc', 'asc'])
    dataAllAttribute[keyId].strongArrData = _.orderBy(dataAllAttribute[keyId].strongArrData, ['total_count', 'fullName'], ['desc', 'asc'])
    dataAllAttribute[keyId].totalArrData = _.orderBy(dataAllAttribute[keyId].totalArrData, ['total_count', 'fullName'], ['desc', 'asc'])
  })

  _.forEach(dataAllAttribute, (item: any, keyId: string) => {
    dataAllAttribute[keyId].powerRating = calculateNetworkPower(item)
  })

  return _.orderBy(Object.values(dataAllAttribute), ['total_count', 'fullName'], ['desc', 'asc'])
}

function removeHydratedConnectionRows(row: any) {
  if (!row || typeof row !== 'object') return
  delete row.innerArrData
  delete row.outerArrData
  delete row.strongArrData
  delete row.totalArrData
}

// Helper functions for management distance calculation
function buildManagementChain(
  userId: string,
  userMaster: any,
  chainCache: Record<string, string[]>,
  maxDepth = 10
): string[] {
  if (chainCache[userId]) {
    return chainCache[userId]
  }

  const chain: string[] = []
  const visited = new Set<string>()
  let currentUserId = userId
  let depth = 0

  while (currentUserId && depth < maxDepth) {
    if (visited.has(currentUserId)) {
      console.warn(`Circular reporting relationship detected: ${userId}`)
      break
    }

    visited.add(currentUserId)
    chain.push(currentUserId)

    const user = userMaster[currentUserId]
    if (!user || !user.boss) {
      break
    }

    currentUserId = user.boss.replace(/\./g, "||")
    depth++
  }

  chainCache[userId] = chain
  return chain
}

function findManagementDistance(
  userId1: string,
  userId2: string,
  userMaster: any,
  chainCache: Record<string, string[]>,
  distanceCache: Record<string, number>
): number {
  if (userId1 === userId2) return 0

  const cacheKey = userId1 < userId2 ? `${userId1}__${userId2}` : `${userId2}__${userId1}`
  if (distanceCache[cacheKey] !== undefined) {
    return distanceCache[cacheKey]
  }

  const chain1 = buildManagementChain(userId1, userMaster, chainCache)
  const chain2 = buildManagementChain(userId2, userMaster, chainCache)
  const chain2Index: Record<string, number> = {}

  for (let i = 0; i < chain2.length; i++) {
    if (chain2Index[chain2[i]] === undefined) {
      chain2Index[chain2[i]] = i
    }
  }

  let minDistance = Infinity
  for (let i = 0; i < chain1.length; i++) {
    const matchIndex = chain2Index[chain1[i]]
    if (matchIndex !== undefined) {
      minDistance = Math.min(minDistance, i + matchIndex)
    }
  }

  distanceCache[cacheKey] = minDistance === Infinity ? 999 : minDistance
  return distanceCache[cacheKey]
}

export default {
  namespaced: true,
  state: {
    page: 1,
    size_page: 10,
    SortField: "",
    SortOrder: "",
    team_id: '',
    selected_nodes: 'initials_team',
    selected_attribute: 'happiness',
    selected_links: 'all_relational_data',
    selected_email: '',  // Email of selected user for focus mode
    loadingRelationshipsTabs: true,
    organization1level: {},
    dataAllAttribute: {},
    teamMembersIndex: {},
    datalist: [],
    datalistAll: [],
    _datalistAll:[],
    dataTableBuildToken: 0,
    profileNetworkCache: {},
    signatures:[
      {
        id: 'brokerage',
        name: 'Brokerage',
        icon: 'sig_brokerage',
        tooltip: 'Brokerage indicates that an individual frequently lies on the shortest path between other individuals in the network. This suggests that the individual plays a significant role as a bridge or broker, connecting disparate groups and potentially controlling the flow of information or resources.',
        color:''
      },{
        id: 'cohesion',
        name: 'Cohesion',
        icon: 'sig_cohesion',
        tooltip: 'Cohesion measures how an individual\'s connections are connected, forming tight-knit groups or clusters. It indicates that an individual\'s connections are likely connected, forming tight-knit groups or clusters. This suggests strong relationships and high levels of collaboration within the individual\'s local network.',
        color:''
      },{
        id: 'connectivity',
        name: 'Connectivity',
        icon: 'sig_connectivity',
        tooltip: 'Connectivity indicates that an individual has high direct connections within the network. This suggests that the individual is well-connected, which can provide them with access to information, resources, and opportunities.',
        color:''
      },{
        id: 'collective',
        name: 'Collective',
        icon: 'sig_collective',
        tooltip: '‌Collective indicates that a person is central and important in the network. These individuals are well-connected, can reach others more quickly, and may have a higher influence on the flow of information in the network.',
        color:''
      },{
        id: 'influence',
        name: 'Influence',
        icon: 'sig_influence',
        tooltip: '‌Influence indicates that an individual is not only well-connected but also connected to other influential individuals. This means that they might have significant influence within the network and can access diverse information through their connections.',
        color:''
      },{
        id: 'reachability',
        name: 'Reachability',
        icon: 'sig_reachability',
        tooltip: 'Reachability indicates that an individual is well-positioned to spread information or influence others efficiently within the network. The individual is, on average, close to all other individuals in the network, which may provide them with better access to information, resources, and opportunities.',
        color:''
      },
      {
        id: 'insularity',
        name: 'Insular',
        icon: 'sig_insularity',
        tooltip: 'Insular indicates that an individual works primarily within their own team silo, with very limited connections outside their immediate group. This suggests they may be missing valuable opportunities for cross-team collaboration and broader organizational impact.',
        color:'-grey'
      },
      {
        id: 'ripple',
        name: 'Ripple',
        icon: 'sig_ripple',
        tooltip: 'Ripple indicates the relative influence this person has by taking into account their direct relationships and how members of the network connect to them through these relationships.',
        color:''
      },
      {
        id: 'hub',
        name: 'Hub',
        icon: 'sig_hub',
        tooltip: 'A Hub is an individual who is both well-connected and can reach others in the network quickly. They serve as a central connecting point in the network.',
        color:'-blue'
      },
      {
        id: 'strategist',
        name: 'Strategist',
        icon: 'sig_strategist',
        tooltip: 'A Strategist is a person who is often on the shortest paths between others and also connected to other influential people. They hold key strategic positions within the network.',
        color:'-blue'
      },
      {
        id: 'community_leader',
        name: 'Community Leader',
        icon: 'sig_communityleader',
        tooltip: 'A Community Leader is an individual who has strong, cohesive relationships with a large number of people. They are often seen as the focal point or leader within their communities.',
        color:'-blue'
      },
      {
        id: 'linker',
        name: 'Linker',
        icon: 'sig_link',
        tooltip: 'A Linker is a powerful bridge builder with considerable influence that ripples throughout the network. They serve as vital links between different communities within the network.',
        color:'-blue'
      },
      {
        id: 'peripheral',
        name: 'Peripheral',
        icon: 'sig_peripheral',
        tooltip: 'Peripheral indicates that an individual has very few direct connections within the network. This suggests limited integration and potential isolation, possibly missing out on information and collaborative opportunities.',
        color: '-grey'
      },
      {
        id: 'distant',
        name: 'Distant',
        icon: 'sig_distant',
        tooltip: 'Distant indicates that an individual is far from others in the network on average. This suggests difficulty in efficiently spreading information or influence, potentially indicating isolation or a role on the network\'s edge.',
        color: '-grey'
      },
    ],
    statusTeam:false
  },
  mutations: {
    setTeamId(state: any, value: any) {
      state.team_id = value.keyword || ''
    },
    setNodes(state: any, value: string) {
      state.selected_nodes = value === 'profile_photo' ? 'initials_team' : value
    },
    setAttribute(state: any, value: string) {
      state.selected_attribute = value
    },
    setLinks(state: any, value: string) {
      state.selected_links = value
    },
    setSelectedEmail(state: any, value: string) {
      state.selected_email = value
    },
    setLoadingRelationshipsTabs(state: any, value: any) {
      state.loadingRelationshipsTabs = value
    },
    setOrganization1level(state: any, value: any) {
      state.organization1level = value
    },
    setDataAllAttribute(state: any, value: any) {
      state.dataAllAttribute = value
    },
    setTeamMembersIndex(state: any, value: any) {
      state.teamMembersIndex = value || {}
    },
    setDataList(state: any, value: any) {
      state.datalist = value || [];
    },
    setDataListAll(state: any, value: any) {
      state.datalistAll = value || [];
    },
    set_DataListAll(state: any, value: any) {
      state._datalistAll = value || [];
    },
    setSortField(state: any, result: any) {
      state.SortField = result
    },
    setSortOrder(state: any, result: any) {
      state.SortOrder = result
    },
    setPage(state: any, result: any) {
      state.page = result
    },
    setDataTableBuildToken(state: any, result: any) {
      state.dataTableBuildToken = result
    },
    setProfileNetworkCache(state: any, result: any) {
      state.profileNetworkCache = Object.assign({}, state.profileNetworkCache || {}, {
        [result.key]: result.value || [],
      })
    },
    setSelectTeam(state: any, result: any){
      state.statusTeam = result
    }
  },
  
  actions: {
    async getOrganization({ commit,state, rootState, dispatch }: any) {
      if(Object.keys(state.organization1level).length == 0){
        let organization = rootState.main.organization
        if(!organization || Object.keys(organization).length === 0){
          organization = await dispatch('main/getOrganization', null, { root: true })
        }

        let teamData = rootState.main.allTeam
        if((!teamData || Object.keys(teamData).length === 0) && rootState.team && rootState.team.teamsall && rootState.team.teamsall.length > 0){
          teamData = mapTeamsByKey(rootState.team.teamsall)
        }
        if(!teamData || Object.keys(teamData).length === 0){
          teamData = await getTeamByCompany(rootState.authen.user.company)
        }

        commit('setOrganization1level', normalizeOrganizationTree(organization, teamData))
      }
    },

    async getAttributeData({commit,dispatch,state,rootState}:any, user:any){
      let userMaster = rootState.main.allUser
      if(!userMaster || Object.keys(userMaster).length === 0) {
        userMaster = await dispatch('main/getuser', false, { root: true })
      }

      let teamMaster = rootState.main.allTeam
      if((!teamMaster || Object.keys(teamMaster).length === 0) && rootState.team && rootState.team.teamsall && rootState.team.teamsall.length > 0){
        teamMaster = mapTeamsByKey(rootState.team.teamsall)
      }
      const relMasterPromise = firebase.database().ref('rel_maps').orderByChild("comp_id").equalTo(user.company).once('value')
      const attributeScoresPromise = fetchRelationshipAttributeScores(user.company).catch((error: any) => {
        console.warn('[relationships] Unable to enrich relationship attributes after base load', error)
        return {}
      })
      if(!teamMaster || Object.keys(teamMaster).length === 0){
        teamMaster = await getTeamByCompany(user.company)
      }
      const relMaster = (await relMasterPromise).val() || {}
      const attributeScoresByEmail:any = {}
      let dataAllAttribute:any = {}
      const teamMembersIndex:any = {}
      const managementChainCache: Record<string, string[]> = {}
      const managementDistanceCache: Record<string, number> = {}
      /* set edges most valuable of rel_weight in the same node */
      const data_relweight:any={}
      _.forEach(relMaster, (item,keyId)=> {
        const userEmail = keyId.replace(/\|\|/g, ".");
        if(userMaster[keyId]){
          const edges = item.edges
          _.forEach(edges, (edges_i)=> {
            if(edges_i.node1 == userEmail || edges_i.node2 == userEmail){
              if(data_relweight[edges_i.node1+"_"+edges_i.node2] == undefined && data_relweight[edges_i.node2+"_"+edges_i.node1] == undefined){
                data_relweight[edges_i.node1+"_"+edges_i.node2] = edges_i.rel_weight
              }else{
                if(data_relweight[edges_i.node1+"_"+edges_i.node2] && data_relweight[edges_i.node1+"_"+edges_i.node2] < edges_i.rel_weight){
                  data_relweight[edges_i.node1+"_"+edges_i.node2] = edges_i.rel_weight
                }else if(data_relweight[edges_i.node2+"_"+edges_i.node1] && data_relweight[edges_i.node2+"_"+edges_i.node1] < edges_i.rel_weight){
                  data_relweight[edges_i.node2+"_"+edges_i.node1] = edges_i.rel_weight
                }
              }
            }
          });
        }
      });
      /* End set edges most valuable of rel_weight in the same node */
      _.forEach(relMaster, (item,keyId)=> {
        const userEmail = keyId.replace(/\|\|/g, ".");
        if(userMaster[keyId]){
          const userData = userMaster[keyId]
          const attributeScores = attributeScoresByEmail[userEmail] || {}
          const metricSources = [attributeScores, item, userData]
          const userObj = Object()
          userObj.firstname = item.firstname
          userObj.lastname = item.lastname
          userObj.fullName = item.firstname + " " + item.lastname
          userObj.cuttingName = item.firstname.charAt(0)+item.lastname.charAt(0)
          userObj.email = userEmail
          userObj.emailKey = keyId
          userObj.profileImage = optionalString(
            item.pic,
            item.profileImage,
            item.profilepic,
            item.profilePic,
            item.large_pic,
            item.largePic,
            userData.pic,
            userData.profileImage,
            userData.profilepic,
            userData.profilePic,
            userData.large_pic,
            userData.largePic,
            userData.avatar,
            userData.photoURL
          )
          userObj.boss = (userData.boss) ? userData.boss : ''
          userObj.teamId = (userData.team) ? userData.team : ''
          userObj.teamName = (userData.team&&teamMaster[userData.team]) ? teamMaster[userData.team].name : ''
          userObj.teamColor = (userData.team&&teamMaster[userData.team]&&teamMaster[userData.team].color) ? teamMaster[userData.team].color : '#747474'
          const impactRating = metricNumberFromSources([attributeScores, userData, item], ['impact_rating', 'impactRating'])
          userObj.impactRating = impactRating === null ? null : Math.round(impactRating)
          userObj.impact_rating = userObj.impactRating
          userObj.healthBar = metricNumberFromSources(metricSources, ['healthbar', 'healthBar', 'happiness'])
          userObj.healthbar = userObj.healthBar
          userObj.who5 = metricNumberFromSources(metricSources, ['who5', 'computed_who5', 'WHO-5', 'wellbeing', 'well_being'])
          userObj.nps = metricNumberFromSources(metricSources, ['nps', 'NPS', 'nps_score', 'npsScore', 'net_promoter_score'])
          userObj.NPS = userObj.nps
          userObj.enps = metricNumberFromSources(metricSources, ['enps', 'eNPS', 'ENPS', 'e_nps', 'enps_score', 'enpsScore', 'employee_nps', 'employee_net_promoter_score'])
          userObj.eNPS = userObj.enps
          userObj.culture = metricNumberFromSources(metricSources, ['culture_index', 'cultureIndex', 'culture', 'culture_score', 'cultureScore'])
          userObj.culture_index = userObj.culture
          userObj.culture_add_percentile = metricNumberFromSources(metricSources, ['culture_add_percentile', 'cultureAddPercentile'])
          userObj.toxic = metricNumberFromSources(metricSources, ['toxic_behaviors', 'toxicBehaviors', 'toxic_behaviour', 'toxicBehaviour', 'toxic_behavior', 'toxicBehavior', 'toxicity', 'toxic', 'toxic_score', 'toxicScore'])
          userObj.toxic_behaviors = userObj.toxic
          // userObj.teamName = _.get(userMaster, `teamMaster[${userMaster[keyId].team}].name`, "")
          userObj.inner_count = 0
          userObj.outer_count = 0
          userObj.strong_count = 0
          userObj.total_count = 0
          userObj.innerMembers = []
          userObj.outerMembers = []
          userObj.strongMembers = []
          userObj.totalMembers = []
          userObj.me_ratio = -1
          userObj.signatures_count = 0
          userObj.signature_groups = []
          userObj.network_roles_count = 0
          if(state.signatures.length > 0){
            userObj.signatures = deriveRelationshipSignatures(item, state.signatures)
            userObj.signatures_count = userObj.signatures.length
            userObj.signature_groups = getRelationshipSignatureGroups(userObj.signatures)
            userObj.network_roles_count = userObj.signature_groups.length
          }
          const edges = item.edges
          const nodeCheck:any=[]
          _.forEach(edges, (edges_i)=> {
            if(edges_i.node1 == userEmail || edges_i.node2 == userEmail){
              if(nodeCheck[edges_i.node1+"_"+edges_i.node2] == undefined && nodeCheck[edges_i.node2+"_"+edges_i.node1] == undefined){
                nodeCheck[edges_i.node1+"_"+edges_i.node2] = true
                if(edges_i.node1 == userEmail){
                  const keyIdNode2 = edges_i.node2.replace(/\./g, "||")
                  if(userMaster[keyIdNode2]){
                    // Use management distance instead of org chart hierarchy
                    const managementDistance = findManagementDistance(keyId, keyIdNode2, userMaster, managementChainCache, managementDistanceCache)
                    if(managementDistance <= 2){
                      userObj.inner_count += 1
                      userObj.innerMembers.push(keyIdNode2)
                    }else{
                      userObj.outer_count += 1
                      userObj.outerMembers.push(keyIdNode2)
                    }
                    if((data_relweight[edges_i.node1+"_"+edges_i.node2] && data_relweight[edges_i.node1+"_"+edges_i.node2] >= 0.66) || (data_relweight[edges_i.node2+"_"+edges_i.node1] && data_relweight[edges_i.node2+"_"+edges_i.node1] >= 0.66)){
                      userObj.strong_count += 1
                      userObj.strongMembers.push(keyIdNode2)
                    }
                    userObj.totalMembers.push(keyIdNode2)
                  }
                }else if(edges_i.node2 == userEmail){
                  const keyIdNode1 = edges_i.node1.replace(/\./g, "||")
                  if(userMaster[keyIdNode1]){
                    // Use management distance instead of org chart hierarchy
                    const managementDistance = findManagementDistance(keyId, keyIdNode1, userMaster, managementChainCache, managementDistanceCache)
                    if(managementDistance <= 2){
                      userObj.inner_count += 1
                      userObj.innerMembers.push(keyIdNode1)
                    }else{
                      userObj.outer_count += 1
                      userObj.outerMembers.push(keyIdNode1)
                    }
                    if((data_relweight[edges_i.node1+"_"+edges_i.node2] && data_relweight[edges_i.node1+"_"+edges_i.node2] >= 0.66) || (data_relweight[edges_i.node2+"_"+edges_i.node1] && data_relweight[edges_i.node2+"_"+edges_i.node1] >= 0.66)){
                      userObj.strong_count += 1
                      userObj.strongMembers.push(keyIdNode1)
                    }
                    userObj.totalMembers.push(keyIdNode1)
                  }
                }
              }
            }
          });
          userObj.total_count = userObj.inner_count + userObj.outer_count
          
          // if(userObj.total_count > 3){
            dataAllAttribute[keyId] = {}
            Object.assign(dataAllAttribute[keyId], userObj);
            if(userObj.teamId){
              if(!teamMembersIndex[userObj.teamId]){
                teamMembersIndex[userObj.teamId] = []
              }
              teamMembersIndex[userObj.teamId].push(dataAllAttribute[keyId])
            }
          // }
        }
      });
      /* Add M/E Ratio to dataAllAttribute */
      _.forEach(dataAllAttribute, (item,keyId)=> {
        if(item.boss){
          const keyIdBoss = item.boss.replace(/\./g, "||")
          if(dataAllAttribute[keyIdBoss] && dataAllAttribute[keyIdBoss].total_count > 0 && dataAllAttribute[keyId].total_count > 0){
            dataAllAttribute[keyId].me_ratio = globalfunc.round(dataAllAttribute[keyIdBoss].total_count / dataAllAttribute[keyId].total_count, 1)
          }
        }
      });

      /* Add arrData to child */
      _.forEach(dataAllAttribute, (item,keyId)=> {
        if(dataAllAttribute[keyId].totalArrData == undefined){
          dataAllAttribute[keyId].totalArrData = []
        }
        if(item.innerMembers.length > 0){
          dataAllAttribute[keyId].innerArrData = []
          _.forEach(item.innerMembers, (itemInnerMember)=> {
            if(dataAllAttribute[itemInnerMember]){
              dataAllAttribute[keyId].innerArrData.push(dataAllAttribute[itemInnerMember])
              dataAllAttribute[keyId].totalArrData.push(dataAllAttribute[itemInnerMember])
            }
          });
          dataAllAttribute[keyId].innerArrData = _.orderBy(dataAllAttribute[keyId].innerArrData, ['total_count','fullName'], ['desc','asc']);
        }
        if(item.outerMembers.length > 0){
          dataAllAttribute[keyId].outerArrData = []
          _.forEach(item.outerMembers, (itemOuterMember)=> {
            if(dataAllAttribute[itemOuterMember]){
              dataAllAttribute[keyId].outerArrData.push(dataAllAttribute[itemOuterMember])
              dataAllAttribute[keyId].totalArrData.push(dataAllAttribute[itemOuterMember])
            }
          });
          dataAllAttribute[keyId].outerArrData = _.orderBy(dataAllAttribute[keyId].outerArrData, ['total_count','fullName'], ['desc','asc']);
        }
        if(item.strongMembers.length > 0){
          dataAllAttribute[keyId].strongArrData = []
          _.forEach(item.strongMembers, (itemStrongMember)=> {
            if(dataAllAttribute[itemStrongMember]){
              dataAllAttribute[keyId].strongArrData.push(dataAllAttribute[itemStrongMember])
            }
          });
          dataAllAttribute[keyId].strongArrData = _.orderBy(dataAllAttribute[keyId].strongArrData, ['total_count','fullName'], ['desc','asc']);
        }
        dataAllAttribute[keyId].totalArrData = _.orderBy(dataAllAttribute[keyId].totalArrData, ['total_count','fullName'], ['desc','asc']);
      });
      // Compute Network Power after direct connection lists exist so the score
      // aligns with the network map's visible neighbors and clusters.
      _.forEach(dataAllAttribute, (item, keyId) => {
        dataAllAttribute[keyId].powerRating = calculateNetworkPower(item);
      });
      // Keep initial store rows shallow. The table and map use member ids;
      // full connected-person rows are only needed if a user opens the
      // connections modal, where the component can hydrate them on demand.
      _.forEach(dataAllAttribute, (item) => {
        removeHydratedConnectionRows(item)
      });
      _.forEach(teamMembersIndex, (members, teamId) => {
        teamMembersIndex[teamId] = _.orderBy(members, ['total_count','fullName'], ['desc','asc']);
      });
      const dataAllAttributeList = _.orderBy(Object.values(dataAllAttribute), ['total_count','fullName'], ['desc','asc']);
      commit("setTeamMembersIndex", teamMembersIndex)
      commit("setDataAllAttribute", dataAllAttributeList)
      dispatch("getDataTable", state.team_id || "")
      attributeScoresPromise.then((scores: any) => {
        if (!scores || Object.keys(scores).length === 0) return
        if (!rootState.authen || !rootState.authen.user || rootState.authen.user.company !== user.company) return

        _.forEach(dataAllAttribute, (item: any) => {
          const attributeScores = scores[item.email] || {}
          applyRelationshipMetricScores(item, [attributeScores, item])
        })

        commit("setDataAllAttribute", _.orderBy(Object.values(dataAllAttribute), ['total_count','fullName'], ['desc','asc']))
        dispatch("getDataTable", state.team_id || "")
      })
    },
    async getProfileNetworkData({ commit, state, rootState }: any, payload: any = {}) {
      const user = payload.user || (rootState.authen && rootState.authen.user) || {}
      const companyId = user.company
      const profileEmail = normalizeEmail(payload.email || payload.emailKey || (payload.profileUser && payload.profileUser.email))
      const profileKey = emailKeyFromEmail(profileEmail)
      const maxNeighbors = Math.max(1, Number(payload.maxNeighbors || 22))

      if (!companyId || !profileEmail || !profileKey) return []

      const cacheKey = `${companyId}:${profileKey}:${maxNeighbors}`
      const cached = state.profileNetworkCache && state.profileNetworkCache[cacheKey]
      if (cached) return cached

      const profileRelMap = (await firebase.database().ref(`/rel_maps/${profileKey}`).once('value')).val()
      if (!relationshipMapMatchesCompany(profileRelMap, companyId)) {
        commit('setProfileNetworkCache', { key: cacheKey, value: [] })
        return []
      }

      const candidateLimit = maxNeighbors
      const candidateNeighborKeys = topNeighborKeysFromRelMap(profileRelMap, profileEmail, candidateLimit)
      const profileFallback = Object.assign({}, payload.profileUser || {}, {
        email: profileEmail,
        company: companyId,
      })
      const userMaster = await fetchProfileUserMaster(
        rootState,
        companyId,
        [profileKey].concat(candidateNeighborKeys),
        profileFallback,
      )
      const neighborKeys = candidateNeighborKeys
        .filter((keyId: string) => Boolean(userMaster[keyId]))
        .slice(0, maxNeighbors)
      const relMaster = await fetchProfileRelMaps(companyId, neighborKeys)
      relMaster[profileKey] = profileRelMap

      let teamMaster = rootState.main && rootState.main.allTeam ? rootState.main.allTeam : {}
      if ((!teamMaster || Object.keys(teamMaster).length === 0) && rootState.team && rootState.team.teamsall && rootState.team.teamsall.length > 0) {
        teamMaster = mapTeamsByKey(rootState.team.teamsall)
      }
      if (!teamMaster || Object.keys(teamMaster).length === 0) {
        teamMaster = await getTeamByCompany(companyId)
      }

      const rows = buildRelationshipRowsFromRelMaps(relMaster, userMaster, teamMaster, state.signatures || [])
      commit('setProfileNetworkCache', { key: cacheKey, value: rows })
      return rows
    },
    async getDataTable({commit,state,rootState}:any, teamId:any){
      const buildToken = Date.now()
      commit('setDataTableBuildToken', buildToken)
      commit('setPage', 1)
      const organization1level = state.organization1level || {}
      const dataAllAttribute = Array.isArray(state.dataAllAttribute) ? state.dataAllAttribute : []
      const isAllTeamsSelection = state.team_id && typeof state.team_id === 'string' && state.team_id.search(/all teams/i) == 0

      if(rootState.authen.user.user_type == 'leadership'){
        if(teamId == "" || isAllTeamsSelection){
          teamId = rootState.authen.user.team
        }
      }

      let result:Array<any> = []
      if(teamId && !isAllTeamsSelection){
        const scopedTeams = organization1level[teamId] || { [teamId]: true }
        commit('setSelectTeam', true)
        _.forEach(scopedTeams, (_item,keyId)=> {
          const teamData:any = state.teamMembersIndex[keyId] || []
          if(teamData.length > 0){
            result.push(...teamData)
          }
        });
      }else{
        commit('setSelectTeam', false)
        result = dataAllAttribute.slice()
      }

      let field = state.SortField
      let order = state.SortOrder
      
      if (order === "ascending") {
        order = 'asc'
      } else {
        order = 'desc'
      }
      if (field === "") {
        field = "total_count"
      }
      
      // For large datasets, defer sorting to prevent blocking
      if (result.length > 500) {
        // Store unsorted data first to show something quickly
        commit('setDataList', result.slice(0, state.size_page));
        commit('setDataListAll', result);
        commit('set_DataListAll', result);
        
        // Then sort asynchronously
        setTimeout(() => {
          if (state.dataTableBuildToken !== buildToken) {
            return
          }
          const sortedResult = _.orderBy(result, [field,'fullName'], [order,'asc']);
          commit('setDataList', sortedResult.slice(0, state.size_page));
          commit('setDataListAll', sortedResult);
          commit('set_DataListAll', sortedResult);
        }, 0);
      } else {
        // For smaller datasets, sort immediately
        result = _.orderBy(result, [field,'fullName'], [order,'asc']);
        if (state.dataTableBuildToken !== buildToken) {
          return
        }
        commit('setDataList', result.slice(0, state.size_page));
        commit('setDataListAll', result);
        commit('set_DataListAll', result);
      }
   
    },
    currentChange({ commit, state }: any, page: any) {
      let size = state.size_page
      let start_row = (page - 1) * size
      let end_row = ((page - 1) * size) + size
      let results: Array<any> = [];
      results = state.datalistAll
      let field = state.SortField
      let order = state.SortOrder
      
      if (order === "ascending") {
        order = 'asc'
      } else {
        order = 'desc'
      }
      if (field === "") {
        field = "total_count"
      }
      results = _.orderBy(results, [field,'fullName'], [order,'asc']);
      commit('setPage', page)
      commit('setDataList', results.slice(start_row, end_row))
    },
    sort({ commit, state }: any, value: any) {
      let results: Array<any> = [];
      results = state.datalistAll
      let size = state.size_page
      let page = state.page
      let start_row = (page-1) * size
      let end_row = ((page-1) * size)+size
      let field = value[0]
      let order = value[1]
      
      if (order === "ascending") {
        order = 'asc'
      } else {
        order = 'desc'
      }
      if (field === "") {
        field = "total_count"
      }
      let _users
      _users = _.orderBy(results, [field,'fullName'], [order,'asc']);
      commit('setSortField', field)
      commit('setSortOrder', order)
      commit('setDataList', _users.slice(start_row,end_row))
    },
    filter({ commit, state }: any, value: any) {
      commit('setPage', 1)
      let results: Array<any> = [];
      // const teamId=value.teamId
      // const organization1level = state.organization1level
      // console.log("value",value)
      if(state.statusTeam==true){
        results = state._datalistAll

      }else{
        results = state.dataAllAttribute
      }

      let size = state.size_page
      let page = state.page
      let start_row = (page-1) * size
      let end_row = ((page-1) * size)+size
      let relation:Array<any>=[]
      // console.log("value",value)
      // console.log("results",results)
      if(value.length>0){
         relation=_.filter(results, function (s) {
          return _.some(s.signatures, function (o) {
            return _.includes(value, o.name);
          });
        });

      }else{
        relation=results
      }
    // console.log("relation",relation)
      commit('setDataList', relation.slice(start_row,end_row))
      commit('setDataListAll', relation);
      
    },
  },
  getters: {
    organization1level: (state: any) => {
      return state.organization1level
    },
    loadingRelationshipsTabs: (state: any) => {
      return state.team_id
    },
    selectTeam: (state: any) => {
      return state.team_id
    },
    datalistAll: (state: any) => {
      return state.datalistAll
    },
    datalist: (state: any) => {
      return state.datalist
    },
    page : (state:any) => {
      return state.page
    },
    signatures : (state:any) => {
      return state.signatures
    },
  }
}
